import { getOrElse } from 'fp-ts/lib/Option';
import { match } from 'ts-pattern';
import { isEmpty } from 'lodash';
import { useState, useEffect, ReactElement, useMemo } from 'react';
// biome-ignore lint/nursery/noRestrictedImports:
import { gql } from '@apollo/client';
import { useTemplatesPreviewQuery, useCampaignsDataQuery } from 'modules/ai/hooks';
import { Select } from 'ui/selectv2/Select';
import {
  StyledAlternateStateCard,
  StyledSelectWrapper,
} from 'modules/intelligentInsights/brandAffinityInsights/components/breakdownByAffinity/cohortTabContent/contributingFactorsCards/ContributionByCampaigns.style';
import {
  StyledCampaignHeaderWithSelectDropdown,
  StyledContributionsCard,
  StyledSubtitlePrimary,
} from 'modules/intelligentInsights/brandAffinityInsights/components/breakdownByAffinity/cohortTabContent/common.styles';

import { useCoreContext } from 'modules/intelligentInsights/brandAffinityInsights/contexts/core/CoreContext';
import { CONTRIBUTION_TOP_FIVE } from 'modules/intelligentInsights/brandAffinityInsights/constants';
import {
  DirectionalityFilterEnum,
  CampaignWithDirectionality,
  ContributionCampaign,
  CampaignImpactAffinityLevels,
} from 'modules/intelligentInsights/brandAffinityInsights/types';
import {
  filterByDirectionality,
  getDirectionalitySelectDropdownOptions,
  mergeCampaignData,
} from 'modules/intelligentInsights/brandAffinityInsights/components/breakdownByAffinity/cohortTabContent/contributingFactorsCards/utils';
import { BrandAffinityLevel } from 'modules/domains';
import { ContributionCampaignList } from 'modules/intelligentInsights/brandAffinityInsights/components/breakdownByAffinity/cohortTabContent/contributingFactorsCards/ContributionCampaignList';
import {
  defaultCampaignImpactCreators,
  useDataManagementContext,
} from 'modules/intelligentInsights/brandAffinityInsights/contexts/dataManagement/DataManagementContext';
import { ContributingCampaignsEmpty } from 'modules/intelligentInsights/brandAffinityInsights/components/breakdownByAffinity/cohortTabContent/contributingFactorsCards/ContributingCampaignsEmpty';
import { ContributingCampaignsError } from 'modules/intelligentInsights/brandAffinityInsights/components/breakdownByAffinity/cohortTabContent/contributingFactorsCards/ContributingCampaignsError';
import { ContributingCampaignsSkeleton } from 'modules/intelligentInsights/brandAffinityInsights/components/breakdownByAffinity/cohortTabContent/contributingFactorsCards/ContributingCampaignsSkeleton';

export const fetchContributionCampaigns = gql`
  query getCampaigns($ids: [Long!]) {
    campaigns(ids: $ids) {
      results {
        id
        messageMedium
        name
        templateId
        campaignTypeDefinition
      }
    }
  }
`;

export const ContributionsByCampaigns = () => {
  const { selectedAffinityTab, fetchCoreData } = useCoreContext();
  const {
    managedInsightsData,
    timeSpan: { selectedTimespan },
  } = useDataManagementContext();
  const [mergedCampaignData, setMergedCampaignData] = useState<CampaignWithDirectionality[]>([]);
  const selectOptions = getDirectionalitySelectDropdownOptions(selectedAffinityTab);
  const [selectedDirectionalityOption, setSelectedDirectionalityOption] = useState([
    selectOptions[0],
  ]);

  const { getSortedCampaigns } = getOrElse(() => defaultCampaignImpactCreators)(
    managedInsightsData.campaignImpact,
  );

  const { all, down, up } = useMemo(
    () => getSortedCampaigns(selectedAffinityTab as CampaignImpactAffinityLevels),
    [selectedAffinityTab, getSortedCampaigns],
  );

  const selectedDirectionEnum = selectedDirectionalityOption[0].value;
  const contributionsByCampaign = match(selectedDirectionEnum)
    .with(DirectionalityFilterEnum.ALLMOVEMENT, () => all)
    .with(DirectionalityFilterEnum.DOWN, () => down)
    .with(DirectionalityFilterEnum.UP, () => up)
    .otherwise(() => all);
  const campaignIds = up
    .concat(down)
    .map((campaignContribution) => campaignContribution.campaignId);
  const {
    isLoading: isFetchCampaignsDataLoading,
    isError: isFetchCampaignsDataError,
    data: campaignData,
  } = useCampaignsDataQuery<ContributionCampaign>(
    fetchContributionCampaigns,
    false,
    false,
    campaignIds,
  );

  useEffect(() => {
    if (isEmpty(campaignData)) {
      return;
    }

    const mergedResults = mergeCampaignData(campaignData, contributionsByCampaign);
    if (selectedDirectionEnum === DirectionalityFilterEnum.ALLMOVEMENT) {
      setMergedCampaignData(mergedResults);
    } else {
      setMergedCampaignData(filterByDirectionality(mergedResults, selectedDirectionEnum));
    }
  }, [campaignData, contributionsByCampaign, selectedDirectionEnum]);

  const parsedTemplateIds = campaignData
    ? campaignData?.map((campaign) => campaign?.templateId)
    : [];

  const {
    isLoading: isTemplatesPreviewLoading,
    isError: previewThumbnailsByIdError,
    data: previewThumbnailsByIdData,
  } = useTemplatesPreviewQuery(
    campaignData !== undefined,
    parsedTemplateIds,
    'NBACampaignsTemplatesPreviews',
  );

  const isSelectDirectionalityDropdownShown =
    selectedAffinityTab === BrandAffinityLevel.POSITIVE ||
    selectedAffinityTab === BrandAffinityLevel.NEUTRAL;

  if (isTemplatesPreviewLoading || isFetchCampaignsDataLoading) {
    return <ContributingCampaignsSkeleton />;
  }

  if (isFetchCampaignsDataError || previewThumbnailsByIdError) {
    return (
      <StyledAlternateStateCard>
        <StyledSubtitlePrimary>{CONTRIBUTION_TOP_FIVE}</StyledSubtitlePrimary>
        <ContributingCampaignsError
          onRefresh={() => {
            fetchCoreData(selectedTimespan);
          }}
        />
      </StyledAlternateStateCard>
    );
  }

  let altStateContent = null;
  if (mergedCampaignData.length === 0) {
    altStateContent =
      campaignIds.length > 0 ? (
        <StyledAlternateStateCard>
          <ContributingCampaignsError
            onRefresh={() => {
              fetchCoreData(selectedTimespan);
            }}
          />
        </StyledAlternateStateCard>
      ) : (
        <ContributingCampaignsEmpty />
      );
  }

  return (
    <StyledContributionsCard data-test={`${selectedAffinityTab}-campaign-contributions`}>
      <StyledCampaignHeaderWithSelectDropdown>
        <StyledSubtitlePrimary>{CONTRIBUTION_TOP_FIVE}</StyledSubtitlePrimary>
        {isSelectDirectionalityDropdownShown && (
          <StyledSelectWrapper>
            <Select
              id={`${selectedAffinityTab}-directionality-selector`}
              isMulti={false}
              options={selectOptions}
              value={selectedDirectionalityOption}
              onSelect={(option) => {
                setSelectedDirectionalityOption([
                  {
                    label: option[0].label as ReactElement<any>,
                    value: option[0].value as DirectionalityFilterEnum,
                  },
                ]);
              }}
              // @ts-ignore
              isSearchable={false}
            />
          </StyledSelectWrapper>
        )}
      </StyledCampaignHeaderWithSelectDropdown>
      {altStateContent || (
        <ContributionCampaignList
          fiteredByDirectionalityCampaignData={mergedCampaignData}
          selectedAffinityTab={selectedAffinityTab}
          previewThumbnailsByIdData={previewThumbnailsByIdData}
        />
      )}
    </StyledContributionsCard>
  );
};
